import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import { getScrollState, subscribeToScroll } from '../../lib/scroll-state'
import { scrollToTop } from '../../lib/smooth-scroll'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE BACK-TO-TOP RAIL — AND THE FOUR THINGS THAT USUALLY BREAK IT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A slim vertical rail on the right edge: an arrow, a hairline, and the word
 * SCROLL running down it. Clicking it returns to the top of the page. Drawn
 * entirely in CSS — no icon import, because `lib/icons.ts` is in the entry
 * graph and this renders on every route.
 *
 * ─── 1. WHERE IT LIVES IN THE TREE, AND WHY IT HAS TO LIVE THERE ───────────
 *
 * `mix-blend-mode` blends an element with the backdrop of its PARENT STACKING
 * CONTEXT — not with "the page". Put this inside anything that creates one —
 * a `transform`, a `filter`, an `opacity` below 1, `will-change`,
 * `isolation: isolate`, another `position: fixed` wrapper — and it blends
 * against that ancestor's empty background instead, which looks exactly like
 * the feature silently not working.
 *
 * So it is rendered from `RootLayout`, as a direct child of the layout's own
 * `<div>`, after the footer. The chain above it is:
 *
 *   html → body → #root → div.flex.min-h-dvh.flex-col → button
 *
 * and NONE of those four creates a stacking context: `#root` is an unstyled
 * div, the layout div is only `display: flex`, and `body`'s `overflow-x: clip`
 * does not create one either. Its parent stacking context is therefore the
 * root element, so its backdrop is the whole painted page.
 *
 * ⚠️ THE BLEND IS ON THE `<button>` ITSELF, NOT ON AN INNER SPAN. It has to
 * be: `position: fixed` creates a stacking context ALL BY ITSELF, regardless
 * of `z-index`, so a blended child of a fixed button would blend against the
 * button's own transparent box and render nothing at all.
 *
 * ⚠️ IF THIS IS EVER MOVED — into a page, into `main`, into any wrapper —
 * check the whole chain again. There is no error and no warning; it just goes
 * the wrong colour, or no colour.
 *
 * ─── 2. THE FOCUS RING CANNOT BE BLENDED ──────────────────────────────────
 *
 * Because the blend is on the button, the global `:focus-visible` outline
 * would be inverted along with everything else — still visible, but no longer
 * the ring colour, and its contrast would then depend on the backdrop exactly
 * as the rail's does. So `:focus-visible` turns the blend OFF and swaps the
 * fill to `currentColor` from the theme: a keyboard user gets a guaranteed
 * contrast rather than a computed one. That is the `focus-visible:` pair on
 * the class list below.
 *
 * ─── 3. IT IS `md` AND UP, WHICH IS A DECISION AND NOT AN OVERSIGHT ───────
 *
 * On a 390 px screen this is 140 px of permanent furniture pinned over the
 * content, along the right edge, which is where a right-handed thumb lives and
 * where our cards' tap targets already reach. The pages it would sit on end in
 * a CTA band and a footer full of links, so a floating control there covers
 * something tappable no matter where it is parked — which the brief forbids.
 * A phone also has the shortest distance back: the momentum flick that got the
 * reader down the page gets them back up it.
 *
 * If the developer wants it on phones, the honest version is a round 44 px
 * button bottom-LEFT (away from the thumb's reach arc and away from the
 * footer's link column), not this rail, and it is a one-line breakpoint change
 * plus a second layout. It is deliberately not guessed at here.
 *
 * ─── 4. SCROLLING IS ROUTED THROUGH LENIS, AND JUMPS UNDER REDUCED MOTION ──
 *
 * `window.scrollTo` fights Lenis's rAF loop and the page visibly snaps back —
 * which is why `lib/smooth-scroll.ts` exists and why `scrollToTop` is the only
 * correct way to do this in this codebase. Under `prefers-reduced-motion` it
 * is called with `immediate: true`: a long animated scroll across a whole
 * document is one of the classic motion-sickness triggers, and this is the one
 * control on the site whose entire job is to travel a long way.
 */
export const BackToTop = () => {
  const { t } = useTranslation()
  const { deep } = useSyncExternalStore(subscribeToScroll, getScrollState)

  return (
    <button
      type="button"
      onClick={() => scrollToTop(window.matchMedia('(prefers-reduced-motion: reduce)').matches)}
      /*
       * ⚠️ `invisible`, NOT `opacity-0`. In the header the opposite was right:
       * those controls still had to be reachable by keyboard while faded, so
       * they kept `opacity-0 pointer-events-none` and the header un-condenses
       * on focus. Here there is nothing to reach — above the threshold this
       * button would scroll to a top the reader is already at — so it must
       * leave the tab order and the accessibility tree entirely, which is what
       * `visibility: hidden` does and `opacity: 0` does not.
       *
       * `visibility` is in the transition list so the hide waits for the fade
       * (a transitioned `visibility` flips as a step at the END of the
       * transition); showing flips it at the start, so the fade-in is visible.
       */
      className={cn(
        'blend-difference fixed right-1 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-2',
        'cursor-pointer px-3 py-4 text-white md:flex',
        'transition-[opacity,visibility,transform] duration-300 ease-out',
        'hover:-translate-y-[calc(50%+2px)]',
        // The one state where the blend is off: see note 2 above.
        'focus-visible:text-foreground focus-visible:[mix-blend-mode:normal]',
        deep ? 'visible opacity-100' : 'invisible opacity-0',
      )}
      aria-label={t('common.backToTop')}
    >
      {/* The arrow, the hairline and the word are all decoration. The button's
          accessible name is the `aria-label` above — a sentence, not "SCROLL",
          and `writing-mode` changes how the word is PAINTED, not how it would
          be read out, so leaving it exposed would announce it as a second,
          confusing label. */}
      <span aria-hidden="true" className="back-to-top-arrow" />
      <span aria-hidden="true" className="back-to-top-line" />
      <span aria-hidden="true" className="back-to-top-word">
        {t('common.scrollWord')}
      </span>
    </button>
  )
}
